/**
 * useEntitlements — THE single hook for plan/entitlements data.
 *
 * DATA LAYER: single fetch of the org's actual entitlements from the
 * organization_entitlements table (via /api/entitlements, React Query).
 * The API normalizes `features` by merging plan defaults under the DB row
 * (legacy rows are missing keys), so `entitlements.features` is complete.
 *
 * GATING LAYER (migrated from use-plan-limits, 2026-10-08): UI gating reads
 * DB entitlements (per-org overrides respected). Keys with no DB column
 * (hasAdvancedReports, hasGranularPermissions) stay derived from the static
 * plan matrix. Demo semantics (user decision 2026-10-08, option A): demo
 * orgs see ALL features view-only — edits are blocked by useDemoMode, not
 * by hiding UI.
 *
 * DISPLAY LAYER: plan name, seat counts/prices and period-based valuation
 * numbers are derived from the static matrix in config/plans.ts — these are
 * commercial facts about the plan tier, not per-org data.
 */

import { useOrganization } from '../contexts/organization-context';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchWithBypass } from '../utils/fetch';
import { logger } from '../utils/logger';
import { getPlan, PlanTier } from '../config/plans';
import { PLAN_FEATURE_MATRIX } from '../billing/entitlements-helpers';
import { useClubSettings } from './use-club-settings';

export interface EntitlementFeatures {
  playerSalesTracking: boolean;
  sponsorshipSponsors: boolean;
  sponsorshipPackages: boolean;
  sponsorshipInventory: boolean;
  sponsorshipProposals: boolean;
  sponsorshipContractApproval: boolean;
  restApi: boolean;
  auditLog: boolean;
  documents: boolean;
  dashboard: boolean;
  valuationTracking: boolean;
  methods: boolean;
  settings: boolean;
}

export interface OrganizationEntitlements {
  organization_id: string;
  plan: 'demo' | 'starter' | 'professional' | 'enterprise';
  billing_period?: 'monthly' | 'yearly'; // Optional for backward compatibility
  is_demo: boolean;
  max_valuations: number;
  max_organizations: number;
  max_users: number;
  purchased_valuations?: number; // Extra valuations purchased (one-time, permanent)
  sso_enabled: boolean; // Single Sign-On (Enterprise only)
  features: EntitlementFeatures;
  reports: {
    basicPdf: boolean;
    brandedPdf: boolean;
    analytics: boolean;
  };
  plugins: {
    marketplace: boolean;
    aiInsights: boolean;
    sankeyChart: boolean;
    universalImporter: boolean;
  };
  support_level: 'none' | 'email' | 'priority' | 'dedicated';
  created_at: string;
  updated_at: string;
}

/**
 * Gating surface: DB feature keys plus the static-only keys that have no
 * DB column and stay derived from config/plans.ts.
 */
export interface GatingFeatures extends EntitlementFeatures {
  hasAdvancedReports: boolean;
  hasGranularPermissions: boolean;
}

/**
 * Hook to get organization entitlements from database
 */
export function useEntitlements() {
  const { organization } = useOrganization();
  const { additionalSeats } = useClubSettings();

  const {
    data: entitlements,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['entitlements', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;

      // Use API endpoint instead of direct Supabase query
      // This bypasses RLS issues with Better Auth vs Supabase Auth
      // Use fetchWithBypass for Preview deployment compatibility
      const response = await fetchWithBypass(`/api/entitlements?organizationId=${organization.id}`);

      if (!response.ok) {
        const errorData = await response.json();

        // 401 Unauthorized means user is not logged in (will be redirected by LayoutWrapper)
        // Return null silently to avoid console errors during redirect
        if (response.status === 401) {
          logger.log(
            '[useEntitlements] User not authenticated, returning null (redirect in progress)'
          );
          return null;
        }

        // Log other errors normally
        logger.error('[useEntitlements] Error fetching entitlements:', errorData);
        throw new Error(errorData.error || 'Failed to fetch entitlements');
      }

      const { entitlements } = await response.json();
      return entitlements as OrganizationEntitlements;
    },
    enabled: !!organization?.id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const isDemo = entitlements?.is_demo ?? false;

  // Plan tier for display/commercial values (demo maps to starter, as before)
  const tier: PlanTier = useMemo(() => {
    const p = entitlements?.plan;
    if (p === 'professional' || p === 'enterprise' || p === 'starter') return p;
    return 'starter';
  }, [entitlements?.plan]);

  const plan = useMemo(() => getPlan(tier), [tier]);

  // Gating tier: demo sees everything (option A) — use the enterprise matrix
  // for keys that have no DB column.
  const gatingTier: PlanTier = isDemo ? 'enterprise' : tier;

  // Combined gating features: plan-matrix defaults under the DB row (DB wins),
  // plus the static-only keys. While loading, falls back to starter defaults —
  // same behavior as the old usePlanLimits.
  const features: GatingFeatures = useMemo(() => {
    const gatingPlan = getPlan(gatingTier);
    return {
      ...PLAN_FEATURE_MATRIX[gatingTier].features,
      ...(entitlements?.features ?? {}),
      hasAdvancedReports: gatingPlan.features.hasAdvancedReports,
      hasGranularPermissions: gatingPlan.features.hasGranularPermissions,
    };
  }, [gatingTier, entitlements?.features]);

  // Seats (display/commercial — from static matrix + purchased additional seats)
  const includedSeats = plan.features.includedSeats;
  const totalSeats = includedSeats + additionalSeats;

  // Valuation limits (period-based, same semantics as the old usePlanLimits)
  const purchasedValuations = entitlements?.purchased_valuations || 0;
  const valuationsPerPeriod = tier === 'enterprise' ? -1 : 1;
  const totalValuationsPerPeriod =
    valuationsPerPeriod === -1 ? -1 : valuationsPerPeriod + purchasedValuations;
  const totalValuations =
    plan.features.valuationsPerYear === -1
      ? -1
      : plan.features.valuationsPerYear + purchasedValuations;

  // Helper functions
  const hasFeature = (feature: keyof OrganizationEntitlements['features']): boolean => {
    return entitlements?.features?.[feature] ?? false;
  };

  const canAccessFeature = (feature: keyof GatingFeatures): boolean => {
    return !!features[feature];
  };

  const hasPlugin = (plugin: keyof OrganizationEntitlements['plugins']): boolean => {
    return entitlements?.plugins?.[plugin] ?? false;
  };

  const hasReport = (report: keyof OrganizationEntitlements['reports']): boolean => {
    return entitlements?.reports?.[report] ?? false;
  };

  const canCreateValuation = (currentCount: number): boolean => {
    if (!entitlements) return false;
    // Unlimited plans (max_valuations = -1): always allowed
    if (entitlements.max_valuations < 0) return true;
    return currentCount < entitlements.max_valuations;
  };

  const canInviteUser = (currentCount: number): boolean => {
    if (!entitlements) return false;
    return currentCount < entitlements.max_users;
  };

  const canCreateOrganization = (currentCount: number): boolean => {
    if (!entitlements) return false;
    return currentCount < entitlements.max_organizations;
  };

  const getRemainingValuations = (currentCount: number): number => {
    if (!entitlements) return 0;
    // Unlimited plans (max_valuations = -1): return -1 (unlimited)
    if (entitlements.max_valuations < 0) return -1;
    return Math.max(0, entitlements.max_valuations - currentCount);
  };

  const getRemainingUsers = (currentCount: number): number => {
    if (!entitlements) return 0;
    return Math.max(0, entitlements.max_users - currentCount);
  };

  const hasReachedSeatLimit = (currentSeats: number): boolean => {
    logger.log('[useEntitlements] Seat limit check:', {
      currentSeats,
      includedSeats,
      additionalSeats,
      limit: totalSeats,
      reached: currentSeats >= totalSeats,
    });
    return currentSeats >= totalSeats;
  };

  const hasReachedValuationLimit = (currentValuationsThisPeriod: number): boolean => {
    // For unlimited plans (-1), never reach limit
    if (valuationsPerPeriod === -1) {
      logger.log('[useEntitlements] Valuation limit check: Unlimited plan, never reached');
      return false;
    }
    logger.log('[useEntitlements] Valuation limit check (period-based):', {
      currentValuationsThisPeriod,
      valuationsPerPeriod,
      purchasedValuations,
      limit: totalValuationsPerPeriod,
      reached: currentValuationsThisPeriod >= totalValuationsPerPeriod,
    });
    return currentValuationsThisPeriod >= totalValuationsPerPeriod;
  };

  return {
    entitlements,
    isLoading,
    error,

    // Plan info
    plan: entitlements?.plan ?? 'starter',
    tier,
    planName: plan.name,
    isDemo,

    // Limits
    maxValuations: entitlements?.max_valuations ?? 0,
    maxUsers: entitlements?.max_users ?? 0,
    maxOrganizations: entitlements?.max_organizations ?? 0,

    // Seats (display)
    includedSeats,
    additionalSeats,
    totalSeats,
    additionalSeatPrice: plan.features.additionalSeatPrice,

    // Valuation limits (period-based)
    valuationsPerPeriod,
    purchasedValuations,
    totalValuations,
    totalValuationsPerPeriod,

    // Gating (DB-driven + static-only keys)
    features,
    reports: entitlements?.reports ?? {},
    plugins: entitlements?.plugins ?? {},
    supportLevel: entitlements?.support_level ?? 'none',

    // Helper functions
    hasFeature,
    canAccessFeature,
    hasPlugin,
    hasReport,
    canCreateValuation,
    canInviteUser,
    canCreateOrganization,
    getRemainingValuations,
    getRemainingUsers,
    hasReachedSeatLimit,
    hasReachedValuationLimit,

    // Legacy compatibility (for gradual migration)
    hasAIInsights: entitlements?.plugins?.aiInsights ?? false,
    hasSankeyChart: entitlements?.plugins?.sankeyChart ?? false,
  };
}
